import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useMutation, useQueryClient } from 'react-query';
import classNames from 'classnames';
import moment from 'moment';

import styles from './styles.module.scss';
import stylesTable from 'styles/table.module.scss';

import { apiResendCandidateMail } from 'api/mailTemplate';
import { useGetMailLogs } from 'hook/useMailTemplate';

import { HTTP_OK } from 'constants/StatusCode.js';
import { LOADING_SM } from 'constants/index';
import { CANDIDATE_PERMISSION } from 'constants/permission';
import { USE_GET_MAIL_LOGS, MAIL_LOG_STATUS, MAIL_LOG_CAN_RESEND } from 'constants/MailTemplate';

import ModalComponent from 'components/Modal';
import Loading from 'components/Loading';
import EditLayout from 'components/PermissionPage/EditLayout';
import MailPreviewModal from 'components/MailEditor/MailPreviewModal';

import { showAlert } from 'helpers/modal';
import { getMailLogStatusText, getMailLogNoteText } from 'helpers/mailTemplate';

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

const formatDateTime = (value) => (value ? moment(value).format(DATE_TIME_FORMAT) : '');

// Mail chờ gửi hiện giờ hẹn, còn lại hiện giờ gửi thực tế (chưa gửi thì giờ tạo)
const getLogTime = (log) =>
  formatDateTime(log.status === MAIL_LOG_STATUS.PENDING ? log.scheduled_at : log.sent_at || log.created_at);

const ModalMailHistory = ({ isShowModal, setShowModal, applyId, onResendDone }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [logView, setLogView] = useState(null);
  const [idResending, setIdResending] = useState(null);

  const { data: listMailLog, isLoading } = useGetMailLogs(applyId, { enabled: !!applyId && isShowModal });

  const { mutate: resendMail } = useMutation((id) => apiResendCandidateMail(id), {
    onMutate: (id) => setIdResending(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries([USE_GET_MAIL_LOGS, applyId]);
      onResendDone(
        response?.data?.status === HTTP_OK
          ? t('candidateMail.resendSuccess')
          : response?.data?.message || t('candidateMail.sendFail'),
      );
    },
    onError: () => {
      showAlert(t('messages.systemError'));
    },
    onSettled: () => setIdResending(null),
  });

  return (
    <>
      <ModalComponent
        toggle={() => setShowModal(false)}
        isShowModal={isShowModal}
        title={t('candidateMail.history')}
        classNameAdd={styles.modalMailHistory}
      >
        <TableContainer className={stylesTable.tableCommon}>
          <Table>
            <TableHead className="asset-header">
              <TableRow className="asset-header-row headTable">
                <TableCell>{t('candidateMail.time')}</TableCell>
                <TableCell>{t('candidateMail.template')}</TableCell>
                <TableCell>{t('managerMailTemplate.subject')}</TableCell>
                <TableCell>{t('candidateMail.to')}</TableCell>
                <TableCell>{t('managerMailTemplate.status')}</TableCell>
                <TableCell>{t('candidateMail.reason')}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Loading addClass="mt-3 mb-3" />
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                listMailLog.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className={styles.cellText}>{getLogTime(log)}</TableCell>
                    <TableCell className={styles.cellText}>
                      {log.template?.name || t('candidateMail.noTemplate')}
                      <div className={styles.cellNote}>
                        {log.is_auto ? t('candidateMail.auto') : t('candidateMail.manual')}
                      </div>
                    </TableCell>
                    <TableCell className={styles.cellText}>{log.subject}</TableCell>
                    <TableCell className={styles.cellText}>
                      {log.to_email}
                      {!!log.is_dry_run && (
                        <div className={styles.cellNote}>
                          <span className={styles.dryRunTag}>{t('candidateMail.dryRun')}</span>
                          {!!log.dry_run_to && t('candidateMail.dryRunTo', { email: log.dry_run_to })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={classNames(styles.logStatus, styles[`logStatus__${log.status}`])}>
                        {getMailLogStatusText(t, log.status)}
                      </span>
                    </TableCell>
                    <TableCell className={styles.cellNote}>{getMailLogNoteText(t, log.note)}</TableCell>
                    <TableCell>
                      <div className={styles.actions}>
                        <span className={styles.link} onClick={() => setLogView(log)}>
                          {t('candidateMail.view')}
                        </span>
                        {MAIL_LOG_CAN_RESEND.includes(log.status) && (
                          <EditLayout permissionName={CANDIDATE_PERMISSION}>
                            {idResending === log.id ? (
                              <Loading type={LOADING_SM} />
                            ) : (
                              <span className={styles.link} onClick={() => !idResending && resendMail(log.id)}>
                                {t('candidateMail.resend')}
                              </span>
                            )}
                          </EditLayout>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              {!isLoading && !listMailLog.length && (
                <tr>
                  <td colSpan={7} className="error-no-data">
                    {t('common.notData')}
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ModalComponent>
      {!!logView && <MailPreviewModal isShowModal={!!logView} setShowModal={() => setLogView(null)} preview={logView} />}
    </>
  );
};

export default ModalMailHistory;
